import { PerformanceAudienceFilterOption } from '@/services/endpoints/performanceAudience';
import { Popover, Transition } from '@headlessui/react';
import { Fragment, FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { AudienceFilterIcon } from '../PlatformBrandIcons';
import {
  paChipClass,
  paChipDisabledClass,
  paChipOpenClass,
  paPopBtnClass,
  paPopClass,
  paPopNodeClass,
  paPopNodeSelectedClass,
} from '../PerformanceAudienceComponents';
import { Button } from '@/lib';
import { cn } from '@/lib/utils';
import { FilterDisabledTooltip } from '../FilterDisabledTooltip';

type AudienceFilterSelectProps = {
  items: PerformanceAudienceFilterOption[];
  selectedId: string;
  onApply: (id: string) => void;
  disabled?: boolean;
  className?: string;
};

export const AudienceFilterSelect: FC<AudienceFilterSelectProps> = ({ items, selectedId, onApply, disabled, className }) => {
  const { t } = useTranslation('performanceAudience');
  const [draftId, setDraftId] = useState(selectedId);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setDraftId(selectedId);
  }, [selectedId]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return items;
    }

    return items.filter((item) => {
      const name = item.value.toLowerCase();
      const id = item.id.toLowerCase();
      return name.includes(query) || id.includes(query);
    });
  }, [items, search]);

  const selected = items.find((item) => item.id === selectedId) ?? null;
  const chipValue = selected?.value ?? t('audienceSelect.noneSelected');
  const isDisabled = disabled || !items.length;

  return (
    <FilterDisabledTooltip disabled={isDisabled} source='audience'>
      <Popover className={twMerge('relative', className)} as='div'>
      {({ open, close }) => (
        <div className={twMerge(paChipClass, open && paChipOpenClass, isDisabled && paChipDisabledClass)}>
          <Popover.Button
            disabled={isDisabled}
            className='flex w-full items-center gap-2 border-0 bg-transparent p-0 text-left outline-none'
            onClick={() => {
              setDraftId(selectedId);
              setSearch('');
            }}
          >
            <span className='shrink-0'>
              <AudienceFilterIcon />
            </span>
            <span className='text-ink-500'>{t('filters.audience')}</span>
            <span className='truncate font-semibold text-ink-900'>{chipValue}</span>
            <svg
              width='12'
              height='12'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              aria-hidden
            >
              <path d='M6 9l6 6 6-6' />
            </svg>
          </Popover.Button>

          <Transition
            show={open}
            as={Fragment}
            enter='transition ease-in duration-100'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='transition ease-in duration-150'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
            afterLeave={() => {
              setDraftId(selectedId);
              setSearch('');
            }}
          >
            <Popover.Panel static className={twMerge(paPopClass, 'w-[380px]')}>
              <div className='relative border-b border-ink-100 px-3.5 pb-2.5 pt-3'>
                <svg
                  className='pointer-events-none absolute left-6 top-1/2 size-3.5 -translate-y-1/2 text-ink-400'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='1.7'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden
                >
                  <circle cx='11' cy='11' r='6' />
                  <path d='m20 20-3.5-3.5' />
                </svg>
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t('audienceSelect.searchPlaceholder')}
                  aria-label={t('audienceSelect.searchPlaceholder')}
                  className='w-full rounded-lg border border-ink-200 bg-ink-50 py-2 pl-8 pr-2.5 text-[13px] text-ink-900 outline-none focus:border-ar-blue focus:bg-white'
                />
              </div>

              <div className='px-3.5 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-400'>{t('audienceSelect.section')}</div>

              <div className='max-h-[380px] overflow-y-auto pb-1.5 pt-0'>
                {filteredItems.length === 0 ? (
                  <div className={cn(paPopNodeClass, 'cursor-default text-ink-500')}>{t('audienceSelect.noResults')}</div>
                ) : (
                  filteredItems.map((item) => {
                    const isSelected = draftId === item.id;
                    return (
                      <div
                        key={item.id}
                        role='option'
                        aria-selected={isSelected}
                        className={cn(paPopNodeClass, isSelected && paPopNodeSelectedClass)}
                        onClick={() => setDraftId(item.id)}
                      >
                        <span className='ml-1 inline-block size-[7px] shrink-0 rounded-full bg-success-600 shadow-[0_0_0_3px_var(--success-100)]' />
                        <div className='min-w-0 flex-1'>
                          <div className={cn('font-medium text-ink-900', isSelected && 'font-semibold text-ar-blue-dark')}>{item.value}</div>
                        </div>
                        {isSelected ? <span className='font-bold text-ar-blue-dark'>✓</span> : null}
                      </div>
                    );
                  })
                )}
              </div>

              <div className='flex items-center justify-between gap-2.5 border-t border-ink-100 px-3.5 py-2.5 text-[12.5px] text-ink-600'>
                <span />
                <Button
                  type='button'
                  intent='primary'
                  className={paPopBtnClass}
                  disabled={!draftId}
                  onClick={() => {
                    if (!draftId) {
                      return;
                    }
                    onApply(draftId);
                    close();
                  }}
                >
                  {t('filters.apply')}
                </Button>
              </div>
            </Popover.Panel>
          </Transition>
        </div>
      )}
    </Popover>
    </FilterDisabledTooltip>
  );
};
